/**
 * Holst Color Combinations - Grid Layout
 * Handles yarn filter, search, accordion, and lightbox
 */

class HolstCombinations {
  constructor() {
    this.container = document.getElementById('holst-combinations');
    if (!this.container) return;

    this.grid = document.getElementById('holst-combinations-grid');
    this.searchInput = this.container.querySelector('.holst-search-input');
    this.filterTabs = this.container.querySelectorAll('.holst-filter-tab');

    this.currentlyOpenCard = null;
    this.currentFilter = 'Titicaca';
    this.searchTerm = '';

    // For reverse lookup
    this.reverseIndex = null;
    this.showingReverse = false;

    // Load translations
    this.translations = this.loadTranslations();

    this.init();
  }

  loadTranslations() {
    const defaults = {
      viewProduct: 'Produkt ansehen',
      showCombinations: 'Kombinationen ansehen',
      combinationsHeading: 'Kombinationen:',
      addToCart: 'In den Warenkorb',
      adding: 'Wird hinzugefügt...',
      added: 'Hinzugefügt!',
      error: 'Fehler',
      zoom: 'Vergrößern',
      noResults: 'Keine Farben gefunden.',
      inStock: 'auf Lager',
      inStockSimple: 'Auf Lager',
      outOfStock: 'Nicht auf Lager'
    };

    const script = document.getElementById('holst-translations');
    if (!script) return defaults;

    try {
      return { ...defaults, ...JSON.parse(script.textContent) };
    } catch (e) {
      return defaults;
    }
  }

  init() {
    this.setupFilterTabs();
    this.setupSearch();
    this.setupAccordions();
    this.setupLightbox();
    this.setupZoomButtons();
    this.setupAddToCart();
    this.buildReverseIndex();
    // Apply initial filter
    this.applyFilters();
  }

  /**
   * Yarn Type Filter Tabs
   */
  setupFilterTabs() {
    this.filterTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const filter = tab.dataset.yarnFilter;
        this.setFilter(filter);

        this.filterTabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
      });
    });
  }

  setFilter(filter) {
    this.currentFilter = filter;

    // Coast and Supersoft show reverse view
    if (filter === 'Coast' || filter === 'Supersoft') {
      this.showReverseView(filter);
    } else {
      this.showForwardView();
    }

    this.applyFilters();
  }

  /**
   * Search Functionality
   */
  setupSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', this.debounce(() => {
      this.searchTerm = this.searchInput.value.toLowerCase().trim();
      this.applyFilters();
    }, 300));
  }

  /**
   * Apply Both Filters
   */
  applyFilters() {
    const cards = this.grid.querySelectorAll('.holst-grid-card');
    let visibleCount = 0;

    cards.forEach(card => {
      const colorName = card.dataset.colorName || '';
      const yarnType = card.dataset.yarnType || '';

      const matchesFilter = this.currentFilter === 'all' || yarnType === this.currentFilter;
      const matchesSearch = !this.searchTerm || colorName.includes(this.searchTerm);

      if (matchesFilter && matchesSearch) {
        card.classList.remove('hidden');
        visibleCount++;
      } else {
        card.classList.add('hidden');
        if (card.classList.contains('is-expanded')) {
          this.closeAccordion(card);
        }
      }
    });

    this.toggleEmptyState(visibleCount === 0);
  }

  toggleEmptyState(show) {
    let emptyState = this.grid.querySelector('.holst-empty-state');

    if (show && !emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'holst-empty-state';
      emptyState.innerHTML = `<p>${this.translations.noResults}</p>`;
      this.grid.appendChild(emptyState);
    } else if (!show && emptyState) {
      emptyState.remove();
    }
  }

  /**
   * Accordion Functionality
   */
  setupAccordions() {
    this.grid.addEventListener('click', (e) => {
      // Don't trigger on zoom button
      if (e.target.closest('[data-zoom-trigger]')) return;

      const trigger = e.target.closest('[data-card-trigger]');
      if (!trigger) return;

      const card = trigger.closest('.holst-grid-card');
      if (!card) return;

      this.toggleAccordion(card);
    });
  }

  toggleAccordion(card) {
    const isExpanded = card.classList.contains('is-expanded');

    if (isExpanded) {
      this.closeAccordion(card);
    } else {
      if (this.currentlyOpenCard && this.currentlyOpenCard !== card) {
        this.closeAccordion(this.currentlyOpenCard);
      }
      this.openAccordion(card);
    }
  }

  openAccordion(card) {
    card.classList.add('is-expanded');
    this.currentlyOpenCard = card;
    this.lazyLoadImages(card);
  }

  closeAccordion(card) {
    card.classList.remove('is-expanded');
    if (this.currentlyOpenCard === card) {
      this.currentlyOpenCard = null;
    }
  }

  lazyLoadImages(container) {
    const images = container.querySelectorAll('img.lazyload[data-src]');
    images.forEach(img => {
      if (!img.src || img.src === '') {
        img.src = img.dataset.src;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      }
    });
  }

  /**
   * Zoom Buttons for Overview Images
   */
  setupZoomButtons() {
    document.addEventListener('click', (e) => {
      const zoomBtn = e.target.closest('[data-zoom-trigger]');
      if (!zoomBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const card = zoomBtn.closest('.holst-grid-card');
      const img = card?.querySelector('.holst-grid-card-image img');
      if (!img) return;

      const src = img.dataset.zoomSrc || img.src;
      const alt = img.alt || '';

      this.openLightbox([{ src, alt }], 0);
    });
  }

  /**
   * Lightbox
   */
  setupLightbox() {
    this.createLightboxHTML();

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox-trigger]');
      if (!trigger) return;

      e.preventDefault();

      const card = trigger.closest('.holst-grid-card');
      const allTriggers = Array.from(card.querySelectorAll('[data-lightbox-trigger]'));

      const items = allTriggers.map(item => ({
        src: item.dataset.pswpSrc,
        alt: item.querySelector('img')?.alt || ''
      }));

      const index = allTriggers.indexOf(trigger);
      this.openLightbox(items, index);
    });
  }

  createLightboxHTML() {
    const lightbox = document.createElement('div');
    lightbox.className = 'holst-lightbox-overlay';
    lightbox.innerHTML = `
      <button class="holst-lightbox-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="holst-lightbox-nav holst-lightbox-prev" aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="holst-lightbox-nav holst-lightbox-next" aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="holst-lightbox-content">
        <img class="holst-lightbox-image" src="" alt="">
      </div>
    `;
    document.body.appendChild(lightbox);

    this.lightbox = lightbox;
    this.lightboxImage = lightbox.querySelector('.holst-lightbox-image');
    this.lightboxPrev = lightbox.querySelector('.holst-lightbox-prev');
    this.lightboxNext = lightbox.querySelector('.holst-lightbox-next');
    this.lightboxClose = lightbox.querySelector('.holst-lightbox-close');

    this.lightboxClose.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrev.addEventListener('click', () => this.navigateLightbox(-1));
    this.lightboxNext.addEventListener('click', () => this.navigateLightbox(1));
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.closeLightbox();
    });

    document.addEventListener('keydown', (e) => {
      if (!this.lightbox.classList.contains('active')) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.navigateLightbox(-1);
      if (e.key === 'ArrowRight') this.navigateLightbox(1);
    });
  }

  openLightbox(items, startIndex = 0) {
    this.lightboxItems = items;
    this.lightboxIndex = startIndex;
    this.showLightboxImage();
    this.lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }

  navigateLightbox(direction) {
    this.lightboxIndex += direction;

    if (this.lightboxIndex < 0) {
      this.lightboxIndex = this.lightboxItems.length - 1;
    } else if (this.lightboxIndex >= this.lightboxItems.length) {
      this.lightboxIndex = 0;
    }

    this.showLightboxImage();
  }

  showLightboxImage() {
    const item = this.lightboxItems[this.lightboxIndex];
    this.lightboxImage.style.opacity = '0';

    setTimeout(() => {
      this.lightboxImage.src = item.src;
      this.lightboxImage.alt = item.alt;
      this.lightboxImage.style.opacity = '1';
    }, 150);

    this.lightboxPrev.disabled = this.lightboxItems.length <= 1;
    this.lightboxNext.disabled = this.lightboxItems.length <= 1;
  }

  /**
   * Build Reverse Index for Coast/Supersoft filters
   */
  buildReverseIndex() {
    const allCards = Array.from(this.grid.querySelectorAll('.holst-grid-card'));

    this.reverseIndex = {
      'Coast': {},
      'Supersoft': {},
      'Titicaca': {}
    };

    allCards.forEach(card => {
      const baseYarnType = card.dataset.yarnType;
      const baseColorName = card.dataset.colorName;
      const baseVariantId = card.dataset.variantId;

      // Only process Titicaca base colors (source of truth)
      if (baseYarnType !== 'Titicaca') return;

      // Get base card info
      const baseThumbnail = card.querySelector('.holst-grid-card-image img');
      const baseProductUrl = card.querySelector('.holst-product-link')?.href || '#';
      const baseStockBadge = card.querySelector('.holst-grid-card-info .holst-stock-badge');
      const baseOverviewImage = baseThumbnail?.dataset?.zoomSrc || baseThumbnail?.src || '';

      // Collect all match items by yarn type
      const matchItems = card.querySelectorAll('.holst-match-item');

      matchItems.forEach(matchItem => {
        const matchLabel = matchItem.querySelector('.holst-match-label');
        if (!matchLabel) return;

        const matchYarnType = matchLabel.textContent.trim();
        if (!matchYarnType || !this.reverseIndex[matchYarnType]) return;

        const matchColorName = matchItem.querySelector('.holst-match-name')?.textContent.trim();
        const matchColorKey = matchColorName ? matchColorName.toLowerCase() : null;

        if (!matchColorKey) return;

        // Initialize entry if doesn't exist
        if (!this.reverseIndex[matchYarnType][matchColorKey]) {
          const matchImage = matchItem.querySelector('.holst-match-image');
          const matchProductUrl = matchItem.querySelector('.holst-view-product-link')?.href || '#';
          const matchStockBadge = matchItem.querySelector('.holst-stock-badge');

          this.reverseIndex[matchYarnType][matchColorKey] = {
            colorName: matchColorName,
            variantImage: matchImage?.dataset?.src || matchImage?.src || '',
            variantImageLarge: matchItem.querySelector('[data-pswp-src]')?.dataset?.pswpSrc || '',
            productUrl: matchProductUrl,
            stockBadgeHTML: matchStockBadge ? matchStockBadge.outerHTML : '',
            matches: []
          };
        }

        // Add the Titicaca base as a match
        this.reverseIndex[matchYarnType][matchColorKey].matches.push({
          yarnType: 'Titicaca',
          colorName: baseColorName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          pairingImage: baseOverviewImage,
          productUrl: baseProductUrl,
          variantId: baseVariantId,
          stockBadgeHTML: baseStockBadge ? baseStockBadge.outerHTML : ''
        });
      });
    });
  }

  /**
   * Show Reverse View (for Coast/Supersoft filters)
   */
  showReverseView(yarnType) {
    if (!this.reverseIndex || !this.reverseIndex[yarnType]) return;

    // Hide original cards
    const originalCards = this.grid.querySelectorAll('.holst-grid-card:not(.holst-reverse-card)');
    originalCards.forEach(card => card.classList.add('hidden'));

    // Remove existing reverse cards
    const existingReverse = this.grid.querySelectorAll('.holst-reverse-card');
    existingReverse.forEach(card => card.remove());

    // Create reverse cards
    const reverseData = this.reverseIndex[yarnType];
    const sortedColors = Object.keys(reverseData).sort();

    sortedColors.forEach(colorKey => {
      const data = reverseData[colorKey];
      const reverseCard = this.createReverseCard(yarnType, data);
      this.grid.appendChild(reverseCard);
    });

    this.showingReverse = true;
  }

  /**
   * Show Forward View (for Titicaca filter)
   */
  showForwardView() {
    const reverseCards = this.grid.querySelectorAll('.holst-reverse-card');
    reverseCards.forEach(card => card.remove());

    const originalCards = this.grid.querySelectorAll('.holst-grid-card');
    originalCards.forEach(card => card.classList.remove('hidden'));

    this.showingReverse = false;
  }

  /**
   * Create Reverse Card HTML
   */
  createReverseCard(yarnType, data) {
    const card = document.createElement('div');
    card.className = 'holst-grid-card holst-reverse-card';
    card.dataset.colorName = data.colorName.toLowerCase();
    card.dataset.yarnType = yarnType;

    const thumbnailSrc = data.variantImage || '';
    const thumbnailLarge = data.variantImageLarge || thumbnailSrc;
    const productUrl = data.productUrl || '#';
    const totalMatches = data.matches ? data.matches.length : 0;

    const matchesHTML = data.matches.map(match => {
      const imageSrc = match.pairingImage || '';
      const colorName = match.colorName || '';
      const viewProductLink = match.productUrl || '#';
      const variantId = match.variantId || '';
      const matchYarnType = match.yarnType || 'Titicaca';
      const stockBadgeHTML = match.stockBadgeHTML || '';
      const isAvailable = stockBadgeHTML.includes('in-stock');

      let addToCartHTML = '';
      if (isAvailable && variantId) {
        addToCartHTML = `
          <button class="holst-add-to-cart-btn" data-variant-id="${variantId}">
            <svg width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" fill="none">
              <path stroke-linecap="round" stroke-miterlimit="10" d="M7.70023 9.14V6.36C7.70023 3.81 9.76023 1.75 12.3002 1.75C14.8402 1.75 16.9102 3.81 16.9102 6.36V9.14M21.8502 19.59L21.1902 9.15C21.1102 7.89 20.0502 6.91 18.7702 6.91H5.83023C4.55023 6.91 3.49023 7.89 3.41023 9.15L2.75023 19.59C2.66023 20.96 3.77023 22.13 5.17023 22.13H19.4402C20.8402 22.13 21.9402 20.97 21.8602 19.59H21.8502Z"></path>
            </svg>
            ${this.translations.addToCart}
          </button>
        `;
      }

      return `
        <div class="holst-match-item">
          <div class="holst-match-image-wrapper" data-lightbox-trigger data-pswp-src="${imageSrc}">
            <img src="${imageSrc}" alt="${colorName}" class="holst-match-image" loading="lazy">
            <div class="holst-image-overlay">
              <svg class="holst-zoom-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35M11 8v6M8 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="holst-overlay-text">${this.translations.zoom}</span>
            </div>
          </div>
          <div class="holst-match-info">
            <p class="holst-match-label">${matchYarnType}</p>
            <p class="holst-match-name">${colorName}</p>
            ${stockBadgeHTML}
            <a href="${viewProductLink}" class="holst-view-product-link">${this.translations.viewProduct} →</a>
            ${addToCartHTML}
          </div>
        </div>
      `;
    }).join('');

    card.innerHTML = `
      <div class="holst-grid-card-image" data-card-trigger>
        ${thumbnailSrc ? `
          <img src="${thumbnailSrc}" alt="${data.colorName}" loading="lazy" data-zoom-src="${thumbnailLarge}">
          <button type="button" class="holst-zoom-btn" data-zoom-trigger aria-label="Vergrößern">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
              <path d="M11 8v6M8 11h6"></path>
            </svg>
          </button>
        ` : ''}
      </div>
      <div class="holst-grid-card-info">
        <h3 class="holst-grid-card-title">${data.colorName}</h3>
        <div class="holst-grid-card-meta">
          <span class="holst-grid-card-badge">${yarnType}</span>
          ${totalMatches > 0 ? `<span class="holst-grid-card-count">${totalMatches} Kombinationen</span>` : ''}
        </div>
        ${data.stockBadgeHTML}
        <button type="button" class="holst-grid-card-expand" data-card-trigger>
          <span>Details</span>
          <svg class="holst-expand-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      </div>
      <div class="holst-grid-card-content" data-card-content>
        <div class="holst-expanded-header">
          <a href="${productUrl}" class="holst-product-link">${this.translations.viewProduct} →</a>
        </div>
        <div class="holst-matches-section">
          <h4 class="holst-matches-title">${this.translations.combinationsHeading}</h4>
          <div class="holst-matches-grid">${matchesHTML}</div>
        </div>
      </div>
    `;

    return card;
  }

  /**
   * Add to Cart
   */
  setupAddToCart() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.holst-add-to-cart-btn');
      if (!btn) return;

      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('adding')) return;

      await this.addToCart(btn, variantId);
    });
  }

  async addToCart(btn, variantId) {
    const originalHTML = btn.innerHTML;
    btn.classList.add('adding');
    btn.innerHTML = this.translations.adding;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Failed');

      btn.classList.remove('adding');
      btn.classList.add('added');
      btn.innerHTML = this.translations.added;

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      setTimeout(() => {
        btn.classList.remove('added');
        btn.innerHTML = originalHTML;
      }, 2000);

    } catch (error) {
      btn.classList.remove('adding');
      btn.innerHTML = this.translations.error;

      setTimeout(() => {
        btn.innerHTML = originalHTML;
      }, 2000);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new HolstCombinations());
} else {
  new HolstCombinations();
}
